/**
 * Detect stale/missing Next.js split chunks and trigger a single full reload.
 * Shared by dashboard listeners and dynamic() import factories.
 */

export const CHUNK_RECOVERY_SESSION_KEY = "hv_dashboard_chunk_recovery";

function chunkFailureMessage(reason: unknown): string {
  if (reason === null || reason === undefined) return "";
  if (typeof reason === "object" && "message" in reason) {
    return String((reason as { message: unknown }).message);
  }
  return String(reason);
}

/** Only Next chunks go stale; a reload never fixes another URL (blocked PDF worker CDN looped). */
function namesForeignUrl(msg: string): boolean {
  const urls: readonly string[] = msg.match(/https?:\/\/[^\s"'()<>]+/g) ?? [];
  return urls.some((url) => !url.includes("/_next/"));
}

export function isChunkLoadFailure(reason: unknown): boolean {
  if (typeof reason === "object" && reason !== null && "name" in reason) {
    const name = String((reason as { name: unknown }).name);
    if (name === "ChunkLoadError") return true;
  }
  const msg = chunkFailureMessage(reason);
  if (!msg) return false;
  if (namesForeignUrl(msg)) return false;
  return (
    (msg.includes("Loading chunk") && msg.includes("failed")) ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("ChunkLoadError") ||
    msg.includes("(missing:") ||
    msg.includes("_next/static/chunks")
  );
}

/**
 * Wraps a dynamic import; on chunk failure schedules one reload (deduped via sessionStorage).
 */
export async function importWithChunkRecovery<T>(
  load: () => Promise<T>,
): Promise<T> {
  try {
    return await load();
  } catch (reason: unknown) {
    if (
      typeof window !== "undefined" &&
      isChunkLoadFailure(reason) &&
      sessionStorage.getItem(CHUNK_RECOVERY_SESSION_KEY) !== "1"
    ) {
      sessionStorage.setItem(CHUNK_RECOVERY_SESSION_KEY, "1");
      window.location.reload();
      return new Promise<T>(() => {});
    }
    throw reason;
  }
}
